export type TranscriptionCaptureMode = 'microphone' | 'microphone-and-system';

export type TranscriptionAudioCapture = {
	stream: MediaStream;
	micStream: MediaStream;
	systemStream: MediaStream | null;
	release: () => void;
	onSystemAudioEnded: (listener: () => void) => () => void;
	replaceSystemAudio: () => Promise<void>;
	removeSystemAudio: () => void;
};

type MutableAudioGraph = {
	audioContext: AudioContext;
	destination: MediaStreamAudioDestinationNode;
	micSource: MediaStreamAudioSourceNode;
	systemSource: MediaStreamAudioSourceNode | null;
};

const resolveMicrophoneCaptureError = (error: unknown): string => {
	if (error instanceof DOMException) {
		if (error.name === 'NotAllowedError') {
			return 'Permita o microfone para este site. No Firefox, clique no ícone à esquerda da barra de endereço e autorize o microfone.';
		}
		if (error.name === 'NotFoundError') {
			return 'Nenhum microfone encontrado. Conecte um microfone e tente novamente.';
		}
	}
	if (error instanceof Error && error.message.trim()) {
		return error.message;
	}
	return 'Não foi possível acessar o microfone.';
};

const resolveDisplayCaptureError = (error: unknown): string => {
	if (error instanceof DOMException) {
		if (error.name === 'NotAllowedError') {
			return 'Permita compartilhar a tela ou aba com áudio do sistema habilitado.';
		}
	}
	if (error instanceof Error && error.message.trim()) {
		return error.message;
	}
	return 'Não foi possível compartilhar a tela com áudio do sistema.';
};

type WindowWithWebkitAudioContext = Window & {
	webkitAudioContext?: typeof AudioContext;
};

const resolveAudioContextClass = (): typeof AudioContext | undefined =>
	window.AudioContext ||
	(window as WindowWithWebkitAudioContext).webkitAudioContext;

const stripVideoTracks = (displayStream: MediaStream) => {
	displayStream.getVideoTracks().forEach((track) => {
		track.stop();
		displayStream.removeTrack(track);
	});
};

const assertSystemAudioTracks = (displayStream: MediaStream) => {
	const systemAudioTracks = displayStream.getAudioTracks();
	if (systemAudioTracks.length === 0) {
		displayStream.getTracks().forEach((track) => track.stop());
		throw new Error(
			'Compartilhe a tela ou aba com áudio do sistema habilitado.',
		);
	}
	return systemAudioTracks;
};

const createMutableAudioGraph = (micStream: MediaStream): MutableAudioGraph => {
	const AudioContextClass = resolveAudioContextClass();
	if (!AudioContextClass || micStream.getAudioTracks().length === 0) {
		throw new Error('Não foi possível inicializar a captura de áudio.');
	}

	const audioContext = new AudioContextClass();
	const destination = audioContext.createMediaStreamDestination();
	const micSource = audioContext.createMediaStreamSource(micStream);
	micSource.connect(destination);
	void audioContext.resume();

	return {
		audioContext,
		destination,
		micSource,
		systemSource: null,
	};
};

const connectSystemSource = (
	graph: MutableAudioGraph,
	systemStream: MediaStream,
) => {
	if (systemStream.getAudioTracks().length === 0) return;

	graph.systemSource?.disconnect();
	const systemSource =
		graph.audioContext.createMediaStreamSource(systemStream);
	systemSource.connect(graph.destination);
	graph.systemSource = systemSource;
};

const teardownAudioGraph = (graph: MutableAudioGraph) => {
	graph.micSource.disconnect();
	graph.systemSource?.disconnect();
	void graph.audioContext.close();
};

const requestMicrophoneStream = async (): Promise<MediaStream> => {
	try {
		return await navigator.mediaDevices.getUserMedia({
			audio: {
				echoCancellation: true,
				noiseSuppression: true,
			},
		});
	} catch (error) {
		throw new Error(resolveMicrophoneCaptureError(error));
	}
};

const requestSystemAudioStream = async (): Promise<MediaStream> => {
	if (!navigator.mediaDevices?.getDisplayMedia) {
		throw new Error(
			'O compartilhamento de áudio do sistema não é suportado neste navegador ou ambiente.',
		);
	}

	try {
		const displayStream = await navigator.mediaDevices.getDisplayMedia({
			video: true,
			audio: true,
		});
		stripVideoTracks(displayStream);
		assertSystemAudioTracks(displayStream);
		return displayStream;
	} catch (error) {
		throw new Error(resolveDisplayCaptureError(error));
	}
};

const buildTranscriptionAudioCapture = (
	micStream: MediaStream,
	systemStream: MediaStream | null,
): TranscriptionAudioCapture => {
	const graph = createMutableAudioGraph(micStream);
	if (systemStream) {
		connectSystemSource(graph, systemStream);
	}

	const systemAudioEndedListeners = new Set<() => void>();
	const systemTrackEndedCleanups: Array<() => void> = [];
	let currentSystemStream = systemStream;

	const notifySystemAudioEnded = () => {
		systemAudioEndedListeners.forEach((listener) => listener());
	};

	const detachSystemTrackListeners = () => {
		systemTrackEndedCleanups.forEach((cleanup) => cleanup());
		systemTrackEndedCleanups.length = 0;
	};

	const attachSystemTrackEndedListeners = (stream: MediaStream | null) => {
		detachSystemTrackListeners();
		if (!stream) return;

		for (const track of stream.getAudioTracks()) {
			const handler = () => notifySystemAudioEnded();
			track.addEventListener('ended', handler);
			systemTrackEndedCleanups.push(() => {
				track.removeEventListener('ended', handler);
			});
		}
	};

	attachSystemTrackEndedListeners(systemStream);

	const release = () => {
		detachSystemTrackListeners();
		teardownAudioGraph(graph);
		micStream.getTracks().forEach((track) => track.stop());
		currentSystemStream?.getTracks().forEach((track) => track.stop());
	};

	return {
		stream: graph.destination.stream,
		micStream,
		get systemStream() {
			return currentSystemStream;
		},
		release,
		onSystemAudioEnded: (listener) => {
			systemAudioEndedListeners.add(listener);
			return () => {
				systemAudioEndedListeners.delete(listener);
			};
		},
		replaceSystemAudio: async () => {
			const nextSystemStream = await requestSystemAudioStream();
			currentSystemStream?.getTracks().forEach((track) => track.stop());
			currentSystemStream = nextSystemStream;
			connectSystemSource(graph, nextSystemStream);
			attachSystemTrackEndedListeners(nextSystemStream);
		},
		removeSystemAudio: () => {
			currentSystemStream?.getTracks().forEach((track) => track.stop());
			currentSystemStream = null;
			graph.systemSource?.disconnect();
			graph.systemSource = null;
			detachSystemTrackListeners();
		},
	};
};

export async function captureMicrophoneAudio(): Promise<TranscriptionAudioCapture> {
	const micStream = await requestMicrophoneStream();
	return buildTranscriptionAudioCapture(micStream, null);
}

export async function captureMicrophoneAndSpeakerAudio(): Promise<TranscriptionAudioCapture> {
	let micStream: MediaStream;
	try {
		micStream = await requestMicrophoneStream();
	} catch (error) {
		throw error;
	}

	let displayStream: MediaStream;
	try {
		displayStream = await requestSystemAudioStream();
	} catch (error) {
		micStream.getTracks().forEach((track) => track.stop());
		throw error;
	}

	return buildTranscriptionAudioCapture(micStream, displayStream);
}

export async function captureTranscriptionAudio(
	mode: TranscriptionCaptureMode,
): Promise<TranscriptionAudioCapture> {
	if (mode === 'microphone') {
		return captureMicrophoneAudio();
	}
	return captureMicrophoneAndSpeakerAudio();
}

export function createTranscriptionAudioCaptureFromStreams(
	micStream: MediaStream,
	systemStream: MediaStream | null,
): TranscriptionAudioCapture {
	return buildTranscriptionAudioCapture(micStream, systemStream);
}
